import React, { useEffect, useMemo, useState } from 'react';
import { LogIn, Filter, AlertTriangle, Loader2 } from 'lucide-react';
import { authFetch } from '../services/api';

const fmtDateTime = (s) =>
  new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

const ROLE_COLOURS = {
  admin: 'text-rose-400',
  investor: 'text-emerald-400',
  auction_house: 'text-amber-400',
  estate_agent: 'text-blue-400',
  deal_source: 'text-purple-400',
};

export default function AdminLoginLog() {
  const [events, setEvents] = useState(null);
  const [error, setError]   = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    authFetch('/api/admin/login-log?limit=500')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { setEvents(d); setError(null); })
      .catch(e => setError(e.message));
  }, []);

  const filtered = useMemo(() => {
    if (!events) return null;
    if (!search.trim()) return events;
    const q = search.trim().toLowerCase();
    return events.filter(e =>
      e.user_email.toLowerCase().includes(q) ||
      e.user_full_name.toLowerCase().includes(q) ||
      (e.ip_address || '').toLowerCase().includes(q),
    );
  }, [events, search]);

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <LogIn size={22} className="text-emerald-400" /> Login Log
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Most recent successful logins, newest first. Limited to the last 500 events.
        </p>
      </div>

      <div className="relative max-w-md">
        <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by email, name, or IP…"
          className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:border-emerald-500 outline-none"
        />
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {!events && !error && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}

      {filtered && filtered.length === 0 && (
        <p className="text-slate-500 text-sm">No login events match.</p>
      )}

      {filtered && filtered.length > 0 && (
        <div className="overflow-hidden bg-slate-900 border border-slate-800 rounded-2xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">When</th>
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-left px-4 py-3 font-medium">IP</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">User-agent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.map(e => (
                <tr key={e.id} className="hover:bg-slate-800/40">
                  <td className="px-4 py-3 text-slate-200 tabular-nums whitespace-nowrap">{fmtDateTime(e.occurred_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-white">{e.user_full_name}</div>
                    <div className="text-slate-500 text-xs">{e.user_email}</div>
                  </td>
                  <td className={`px-4 py-3 capitalize ${ROLE_COLOURS[e.user_role] || 'text-slate-400'}`}>
                    {e.user_role.replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{e.ip_address || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs truncate max-w-md hidden lg:table-cell" title={e.user_agent || ''}>
                    {e.user_agent || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-slate-950/50 text-xs text-slate-500 border-t border-slate-800">
            Showing {filtered.length} of {events.length} events
          </div>
        </div>
      )}
    </div>
  );
}
